'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import Header from './Header';
import BottomNav from './BottomNav';
import AlertToaster from './AlertToaster';
import FirstVisitGuide from './FirstVisitGuide';
import NewbieHint from './NewbieHint';
import NetworkStatus from './NetworkStatus';
import RouteTransition from './RouteTransition';
import { useAppStore } from '@/store/app';
import { api, getAccessToken, setAuthTokens, AUTH_UNAUTHORIZED_EVENT } from '@/lib/api';
import { loadFxRates } from '@/lib/format';
import type { PublicUser } from '@glintfans/shared';

/** Load FX rates on app start so prices can convert to selected currency. */
function FxLoader() {
  useEffect(() => { loadFxRates(); }, []);
  return null;
}

/** Applies the persisted theme to <html> so Tailwind dark/light classes take effect. */
function ThemeApplier() {
  const theme = useAppStore((s) => s.theme);

  useEffect(() => {
    const el = document.documentElement;
    el.classList.remove('dark', 'light');
    el.classList.add(theme === 'light' ? 'light' : 'dark');
  }, [theme]);

  return null;
}

/** Sets the browser tab title per language: zh-Hant shows the Chinese slogan. */
function TitleApplier() {
  const language = useAppStore((s) => s.language);
  const pathname = usePathname();

  useEffect(() => {
    const title =
      language !== 'en'
        ? 'AiLumix-波动可见，决策有据'
        : 'AiLumix - See Volatility, Decide with Confidence';
    // Next's metadata manager writes <title> on route commits, which can land
    // after this effect, so re-apply on a short delay as well.
    document.title = title;
    const t1 = window.setTimeout(() => { document.title = title; }, 100);
    const t2 = window.setTimeout(() => { document.title = title; }, 600);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [language, pathname]);

  return null;
}

/** Keeps <html lang> in sync with the selected language (SSR default is en). */
function LangApplier() {
  const language = useAppStore((s) => s.language);

  useEffect(() => {
    document.documentElement.lang = language !== 'en' ? 'zh-Hant' : 'en';
  }, [language]);

  return null;
}

/** Restores the logged-in user from persisted tokens after a page refresh.
    Auth state is explicit: checking → authenticated | guest, never flash. */
function SessionRestore() {
  const setUser = useAppStore((s) => s.setUser);
  const setAuthState = useAppStore((s) => s.setAuthState);

  useEffect(() => {
    if (!getAccessToken()) {
      setAuthState('guest');
      return;
    }
    setAuthState('checking');
    api
      .get<PublicUser>('/users/me')
      .then((u) => {
        setUser(u);
        setAuthState('authenticated');
      })
      .catch(() => {
        setAuthTokens(null);
        setUser(null);
        setAuthState('guest');
      });
  }, [setUser, setAuthState]);

  return null;
}

/** Marks the session as expired when the stored token is rejected by the API. */
function SessionGuard() {
  const setUser = useAppStore((s) => s.setUser);
  const setAuthState = useAppStore((s) => s.setAuthState);

  useEffect(() => {
    const handler = () => {
      setUser(null);
      setAuthState('expired');
      window.setTimeout(() => setAuthState('guest'), 3000);
    };
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handler);
    return () => window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handler);
  }, [setUser, setAuthState]);

  return null;
}

export default function Providers({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  // Secure admin entry: /admin was renamed to an unguessable path.
  const isAdmin = pathname?.startsWith('/portal-4uectg19');
  const isAuthPage = pathname === '/login' || pathname === '/register';

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 15_000,
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeApplier />
      <TitleApplier />
      <LangApplier />
      <FxLoader />
      <SessionRestore />
      <SessionGuard />
      <AlertToaster />
    {!isAdmin && <FirstVisitGuide />}
    {!isAdmin && <NewbieHint />}
      {!isAdmin && <Header />}
      <main className={isAdmin ? '' : 'mx-auto max-w-[1440px] px-4 py-4 pb-20 lg:px-6 md:pb-4'}>
        <RouteTransition>{children}</RouteTransition>
      </main>
      {!isAdmin && !isAuthPage && <BottomNav />}
      <NetworkStatus />
    </QueryClientProvider>
  );
}
